import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { projectsApi, workQueryKeys } from '@/features/work/api';
import type {
  ProjectsFilters,
  CreateProjectPayload,
  UpdateProjectPayload,
} from '../types';

// ─── Queries ────────────────────────────────────────────────────────────────

export function useProjects(filters: ProjectsFilters = {}) {
  return useQuery({
    queryKey: workQueryKeys.projects.list(filters),
    queryFn: () => projectsApi.list(filters),
    placeholderData: keepPreviousData,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useProjectMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: workQueryKeys.projects.root });

  const createProject = useMutation({
    mutationFn: (payload: CreateProjectPayload) => projectsApi.create(payload),
    onSuccess: invalidate,
  });

  const updateProject = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateProjectPayload }) => projectsApi.update(id, payload),
    onSuccess: invalidate,
  });

  const deleteProject = useMutation({
    mutationFn: (id: string) => projectsApi.remove(id),
    onSuccess: invalidate,
  });

  return { createProject, updateProject, deleteProject };
}
